import { useState } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { useAuth } from '@/context/AuthContext';

export interface CommunityPost {
  id: string;
  user_id: string;
  title: string;
  content: string;
  category: string;
  category_label: string;
  tags: string[];
  is_prediction: boolean;
  prediction_market: string | null;
  prediction_odds: number | null;
  prediction_result: string | null;
  is_pinned: boolean;
  views: number;
  likes_count: number;
  comments_count: number;
  created_at: string;
  profiles: { username: string; plan: string } | null;
  userLiked?: boolean;
}

interface PostCardProps {
  post: CommunityPost;
  onLikeToggle: (postId: string, liked: boolean) => void;
  onDelete?: (postId: string) => void;
}

function timeAgo(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diff = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (diff < 60) return 'Hace un momento';
  if (diff < 3600) return `Hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `Hace ${Math.floor(diff / 3600)} h`;
  if (diff < 604800) return `Hace ${Math.floor(diff / 86400)} días`;
  return date.toLocaleDateString('es-ES');
}

const PLAN_BADGE: Record<string, string> = {
  elite: 'Elite',
  premium: 'Pro',
  free: 'Activo',
};
const PLAN_COLOR: Record<string, string> = {
  elite: 'bg-[#ffd700]/15 text-[#ffd700]',
  premium: 'bg-[#00e87a]/15 text-[#00e87a]',
  free: 'bg-white/5 text-[#a8b2c1]',
};

export default function PostCard({ post, onLikeToggle, onDelete }: PostCardProps) {
  const { isRegistered, supabaseUser, openLoginModal } = useAuth();
  const [isLiking, setIsLiking] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const plan = post.profiles?.plan ?? 'free';
  const username = post.profiles?.username ?? 'Anónimo';
  const badge = PLAN_BADGE[plan] ?? 'Activo';
  const badgeColor = PLAN_COLOR[plan] ?? PLAN_COLOR.free;
  const isOwner = isRegistered && supabaseUser?.id === post.user_id;

  const handleLike = async () => {
    if (!isRegistered || !supabaseUser) { openLoginModal(); return; }
    if (isLiking) return;
    setIsLiking(true);
    try {
      if (post.userLiked) {
        await supabase.from('post_likes').delete().eq('post_id', post.id).eq('user_id', supabaseUser.id);
        onLikeToggle(post.id, false);
      } else {
        await supabase.from('post_likes').insert({ post_id: post.id, user_id: supabaseUser.id });
        onLikeToggle(post.id, true);
      }
    } finally {
      setIsLiking(false);
    }
  };

  const handleDelete = async () => {
    if (!supabaseUser) return;
    setIsDeleting(true);
    await supabase.from('posts').delete().eq('id', post.id).eq('user_id', supabaseUser.id);
    setIsDeleting(false);
    setConfirmDelete(false);
    if (onDelete) onDelete(post.id);
  };

  return (
    <>
      <div className={`bg-[#151719] border rounded-xl p-5 hover:border-white/20 transition-all cursor-default ${
        post.is_pinned ? 'border-[#00e87a]/20' : 'border-[#2a2d3a]'
      }`}>
        {post.is_pinned && (
          <div className="flex items-center gap-1.5 text-[#00e87a] text-xs font-bold mb-3">
            <div className="w-3 h-3 flex items-center justify-center">
              <i className="ri-pushpin-line text-xs"></i>
            </div>
            FIJADO
          </div>
        )}

        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-full bg-[#2a2d3a] flex items-center justify-center flex-shrink-0">
            <span className="text-white text-sm font-bold uppercase">{username.slice(0, 2)}</span>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="text-white font-semibold text-sm">{username}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${badgeColor}`}>{badge}</span>
              <span className="px-2 py-0.5 rounded-full text-xs bg-[#1e2028] text-[#a8b2c1]">{post.category_label}</span>
              <span className="text-[#6b7280] text-xs ml-auto">{timeAgo(post.created_at)}</span>
              {isOwner && (
                <button
                  onClick={() => setConfirmDelete(true)}
                  className="w-6 h-6 flex items-center justify-center text-[#6b7280] hover:text-[#ef4444] hover:bg-[#ef4444]/10 rounded-md transition-all cursor-pointer"
                  title="Eliminar post"
                >
                  <i className="ri-delete-bin-line text-xs"></i>
                </button>
              )}
            </div>

            <h3 className="text-white font-bold text-base mb-2 leading-snug">{post.title}</h3>
            <p className="text-[#a8b2c1] text-sm line-clamp-2 leading-relaxed">{post.content}</p>

            {post.is_prediction && post.prediction_market && (
              <div className={`inline-flex items-center gap-2 mt-3 px-3 py-1.5 rounded-lg border text-xs font-bold ${
                post.prediction_result === 'won'  ? 'bg-[#00e87a]/10 border-[#00e87a]/20 text-[#00e87a]' :
                post.prediction_result === 'lost' ? 'bg-[#ef4444]/10 border-[#ef4444]/20 text-[#ef4444]' :
                'bg-[#f59e0b]/10 border-[#f59e0b]/20 text-[#f59e0b]'
              }`}>
                <div className="w-3 h-3 flex items-center justify-center">
                  <i className={`${
                    post.prediction_result === 'won'  ? 'ri-check-line' :
                    post.prediction_result === 'lost' ? 'ri-close-line' : 'ri-time-line'
                  } text-xs`}></i>
                </div>
                PICK: {post.prediction_market}
                {post.prediction_odds && <span> @ {post.prediction_odds}</span>}
                {post.prediction_result === 'won'     && <span className="ml-1">· GANADO</span>}
                {post.prediction_result === 'lost'    && <span className="ml-1">· PERDIDO</span>}
                {post.prediction_result === 'pending' && <span className="ml-1">· PENDIENTE</span>}
              </div>
            )}

            {post.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {post.tags.map((tag) => (
                  <span key={tag} className="px-2 py-0.5 rounded-md text-xs bg-[#1e2028] text-[#6b7280]">#{tag}</span>
                ))}
              </div>
            )}

            <div className="flex items-center gap-5 mt-4 pt-4 border-t border-[#1e2028]">
              <button
                onClick={handleLike}
                disabled={isLiking}
                className={`flex items-center gap-1.5 text-xs font-medium transition-colors cursor-pointer ${
                  post.userLiked ? 'text-[#00e87a]' : 'text-[#6b7280] hover:text-white'
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${post.userLiked ? 'ri-heart-fill' : 'ri-heart-line'} text-sm`}></i>
                </div>
                {post.likes_count}
              </button>
              <span className="flex items-center gap-1.5 text-xs text-[#6b7280]">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-chat-3-line text-sm"></i>
                </div>
                {post.comments_count} respuestas
              </span>
              <span className="flex items-center gap-1.5 text-xs text-[#3d4149]">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-eye-line text-sm"></i>
                </div>
                {post.views.toLocaleString('es-ES')}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Confirm delete modal */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 px-4">
          <div className="bg-[#151719] border border-[#2a2d3a] rounded-2xl p-6 max-w-sm w-full text-center">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#ef4444]/15 mx-auto mb-4">
              <i className="ri-delete-bin-line text-[#ef4444] text-xl"></i>
            </div>
            <h3 className="text-white font-bold text-base mb-2">¿Eliminar este post?</h3>
            <p className="text-[#6b7280] text-sm mb-6">Esta acción no se puede deshacer.</p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmDelete(false)}
                className="flex-1 py-2.5 border border-[#2a2d3a] text-[#a8b2c1] text-sm font-medium rounded-xl hover:bg-white/5 cursor-pointer whitespace-nowrap transition-all"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="flex-1 py-2.5 bg-[#ef4444] text-white text-sm font-bold rounded-xl hover:bg-[#dc2626] cursor-pointer whitespace-nowrap transition-all disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {isDeleting ? (
                  <><i className="ri-loader-4-line text-sm animate-spin"></i> Eliminando...</>
                ) : 'Sí, eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
